import { useMemo, useRef, useState } from 'react';
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    arrow,
    useFocus,
    useHover,
} from '@floating-ui/react';

type Interaction = 'click' | 'focus' | 'hover';

export interface PopoverOptions {
    /**
     * The distance the popover content should appear from the reference trigger.
     * @default 10
     */
    pixelOffset?: number;
    /**
     * Determines if the popover content is visible.
     * (required for controlled popover)
     */
    open?: boolean;
    /**
     * Callback fired whenever the open prop is changed
     * (required for controlled popover)
     */
    onOpenChange?: (open: boolean) => void;
    /**
     * What interaction on the trigger should open the popover
     * @default 'click'
     */
    triggerInteractions?: Interaction[];
}

export function usePopover({
    open: controlledOpen,
    onOpenChange: setControlledOpen,
    pixelOffset = 10,
    triggerInteractions = ['click'],
}: PopoverOptions = {}) {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const arrowRef = useRef<SVGSVGElement>(null);

    const isOpen = controlledOpen ?? uncontrolledOpen;
    const setIsOpen = setControlledOpen ?? setUncontrolledOpen;

    const {
        refs,
        floatingStyles,
        context: floatingContext,
    } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: 'bottom',
        middleware: [offset(pixelOffset), flip(), shift(), arrow({ element: arrowRef })],
        whileElementsMounted: autoUpdate,
    });

    const interactionSet = new Set(triggerInteractions);
    const click = useClick(floatingContext, { enabled: interactionSet.has('click') });
    const focus = useFocus(floatingContext, { enabled: interactionSet.has('focus') });
    const hover = useHover(floatingContext, { enabled: interactionSet.has('hover') });

    const dismiss = useDismiss(floatingContext);
    const role = useRole(floatingContext);

    const { getReferenceProps, getFloatingProps } = useInteractions([
        click,
        focus,
        hover,
        dismiss,
        role,
    ]);

    return useMemo(
        () => ({
            isOpen,
            setIsOpen,
            refs,
            floatingStyles,
            floatingContext,
            getReferenceProps,
            getFloatingProps,
            arrowRef,
        }),
        [
            isOpen,
            setIsOpen,
            refs,
            floatingStyles,
            floatingContext,
            getReferenceProps,
            getFloatingProps,
            arrowRef,
        ],
    );
}
